import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { appointments, appointmentDetail, cancelAppointment } from "../../redux/reducers/AppoinmentSlice";
import { FaEye } from "react-icons/fa";
import { IoIosCloseCircleOutline } from "react-icons/io";
import Toast from "../../utils/toast";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const Appointments = ({ setActiveTab, setSelectedAppointment }) => {
  const dispatch = useDispatch();
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate()

  useEffect(() => {
    if (!appointmentsData || !Array.isArray(appointmentsData)) {
      setFilteredAppointments([]);
      return;
    }
    
    const filteredData = appointmentsData.filter((appointment) =>
      appointment.doctor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.date.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.appointment_id.toString().includes(searchTerm) ||
      appointment.appointment_status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (appointment.language && appointment.language.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredAppointments(filteredData);
  }, [searchTerm, appointmentsData]);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const payload = { page: currentPage };
        const responseAppointment = await dispatch(appointments(payload));
        setAppointmentsData(responseAppointment.payload.data);
        setTotalPages(responseAppointment.payload.total_pages);
      } catch (error) {
        // Error fetching data
      }
    };
    fetchAppointments();
  }, [dispatch, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleViewClick = async (id) => {
    try {
      const response = await dispatch(appointmentDetail({ appointment_id: id }));
      if (response.payload) {
        setSelectedAppointment(response.payload);
        setActiveTab("appointment");
        navigate(`/dashboard/appointment/${id}`);
      }
    } catch (error) {
      // Error fetching appointment details
    }
  };

  const handleCancelClick = (id) => {
    setSelectedAppointmentId(id);
    setShowModal(true);
  };

  const confirmCancellation = async () => {
    if (!cancelReason.trim()) {
      Toast("error", "Please enter a reason for cancellation");
      return;
    }

    try {
      const response = await dispatch(cancelAppointment({
        appointment_id: selectedAppointmentId,
        cancel_reason: cancelReason
      }));

      if (response.payload.success) {
        Toast("success", "Appointment cancelled successfully");
        // Refresh the appointments list to show updated status
        const payload = { page: currentPage };
        const responseAppointment = await dispatch(appointments(payload));
        setAppointmentsData(responseAppointment.payload.data);
        setShowModal(false);
        setCancelReason(""); // Reset the input field after cancellation
      } else {
        Toast("error", response.payload.message || "Failed to cancel appointment");
      }
    } catch (error) {
      // Error cancelling appointment
    }
  };

  return (
    <>
      <div className="appointment_history">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <h5 style={{ margin: 0 }}>Appointment History</h5>
          <input
            type="text"
            className="search-bar"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "250px",
              padding: "5px",
              borderRadius: "5px",
              border: "1px solid #ccc"
            }}
          />
        </div>


        <div className="table-responsive">
          <table className="table">
            <tbody>
              <tr>
                <th className="sn">#</th>
                <th className="name">Doctor</th>
                <th className="date">Appointment Date/Time</th>
                <th className="chamber">Appointment ID</th>
                <th className="language">Language</th>
                <th className="status">Status</th>
                <th className="action">Action</th>
              </tr>
              {filteredAppointments?.map((appointment, index) => (
                <tr key={index} className="table_row">
                  <td className="sn">
                    <p>{index + 1}</p>
                  </td>
                  <td className="name">
                    <p>{appointment.doctor_name}</p>
                  </td>
                  <td className="date">
                    <p>{appointment.date}</p>
                    <span className="date_time">{appointment.time}</span>
                  </td>
                  <td className="chamber">
                    <p>{appointment.appointment_id} </p>
                  </td>
                  <td className="language">
                    <p>{appointment.language || 'N/A'}</p>
                  </td>
                  <td className="status">
                    <span className={`status-badge ${appointment.appointment_status ? appointment.appointment_status.toLowerCase() : 'unknown'}`}>
                      {appointment.appointment_status ? appointment.appointment_status.replace(/_/g, " ") : 'Unknown'}
                    </span>
                    {appointment.appointment_status === 'cancel' && appointment.cancel_by && appointment.cancel_reason && (
                      <div style={{ fontSize: '12px', marginTop: '5px', color: '#dc3545' }}>
                        <div><strong>Cancelled by:</strong> {appointment.cancel_by}</div>
                        <div><strong>Reason:</strong> {appointment.cancel_reason}</div>
                      </div>
                    )}
                  </td>
                  <td className="action">
                    <button className="view-btn" onClick={() => handleViewClick(appointment.id)}>
                      <FaEye size={16} />
                    </button>


                    {appointment.zoom_meeting_status === 1 && (
                      <>
                        <a
                          href={appointment.zoom_meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <button className="meeting-btn">
                            Join Meeting
                          </button>
                        </a>
                      </>
                    )}


                    {appointment.is_show_cancel === 1 && (
                      <button className="cancel-btn" onClick={() => handleCancelClick(appointment.id)}>
                        Cancel <IoIosCloseCircleOutline size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="row mt_60">
          <div className="col-12">
            <div id="pagination">
              <nav aria-label="...">
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                      <i className="fas fa-arrow-left"></i>
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                    <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(page)}>
                        {page}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Cancellation Confirmation Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>Cancel</h3>
            <p>Are you sure you want to cancel this appointment?</p>
            <textarea
              className="cancel-reason-input"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter cancellation reason..."
              required />
            <div className="modal-actions">
              <button className="btn btn-danger" onClick={confirmCancellation}>Yes, Cancel</button>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>No, Keep</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Appointments;